/**
 * THE WORDS ON THE CHART.
 *
 * Kept apart from the painter so the wording can be read, argued with and tested without a
 * canvas. Three rules it keeps, from the design law and from what this project has already
 * been told:
 *
 *   IT IS A PHRASE, NOT A FIELD. "Over cold water, drifting north-east" rather than
 *   "REGION: cold  HEADING: 042". A label-and-value pair is an instrument panel; this is
 *   meant to be a chart somebody wrote on.
 *   IT SPEAKS IN THE INTERFACE'S VOICE. The hold's disclosure states what happened and does
 *   not apologise for it, because nothing went wrong: the hold is the mechanism that makes
 *   the offline invariant literally true, and CONTRACT section 6 requires it be disclosed.
 *   IT NAMES NOTHING THE PLAYER HAS NOT FOUND. Inside the charted sea the voyage layer
 *   withholds the next place's name and publishes only its bearing; this copy does the same.
 *   A FIGURE WEARS THE UNIT OF THE THING IT MEASURES. Added round 4, and it is the rule the
 *   sheet broke worst. "Land bears south, 656 leagues off" put a NAVIGATIONAL unit on a
 *   PROGRESSION quantity, and the two disagreed by up to 931x with land the player could see
 *   on screen. A wrong number gets caught; a wrong unit gets trusted. So: leagues are only
 *   ever written against a distance between two points in the world, and progression is
 *   written in the unit progression is felt in, which is time. `chart-units.test.ts` sweeps
 *   the reachable states and fails on any figure whose unit its own source cannot back.
 */

import { SPECIES, levelForXp, speciesById, type SimState } from '../sim/index';
import {
  bearingDeg,
  type ChartRow,
  type SailRefusalReason,
  type VoyageView,
  type WaterRegionId,
} from '../voyage/index';
import type { LedgerRow } from './chart';

const COMPASS = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];

/**
 * IS THIS A NAME, OR IS IT SCAFFOLDING?
 *
 * Written when `src/voyage/places.ts` was headed "PLACEHOLDER PLACE TABLE" and said its labels
 * were "deliberately non-fiction so nobody mistakes this for design work". The first build
 * printed "PLACEHOLDER 15 bears north-west." on the chart, which is scaffolding shown to a
 * player, so the chart began withholding it exactly the way the voyage layer already withholds
 * the name of a place the boat has not raised: the bearing is published, the name is not.
 *
 * CONTRACT 1.5m IS THAT RULING AND IT HAS LANDED. `places.ts` is now headed "TWENTY PLACES,
 * TWENTY NAMES, FIVE AREAS. NO PLACEHOLDERS LEFT", and all twenty came through this function
 * with no edit to it - which is the seam working, not the seam being spent. It stays, because
 * the day a twenty-first place is charted before it is named this is what keeps the
 * scaffolding off the sheet.
 */
function isNamed(label: string | null): label is string {
  return label !== null && label.length > 0 && !/^placeholder/i.test(label);
}

export { isNamed };

/** Bearing degrees to an eight-point name. 0 is north and it turns clockwise. */
export function compass(deg: number): string {
  const d = ((deg % 360) + 360) % 360;
  return COMPASS[Math.round(d / 45) % 8] ?? 'north';
}

/**
 * THE FOUR WATERS, SAID AS QUALITIES OF WATER AND NOT AS PLACES.
 *
 * `src/voyage/regions.ts` derives the water at a point from two continuous fields -
 * `openness`, which is distance from the world centre, and `chill`, which is latitude - and
 * says of the four ids in its own header: "NAMES ARE DESCRIPTIVE, NOT FICTION. `story` and
 * `world-places` own what these waters are called in front of a player."
 *
 * The first two phrases here took the descriptive ids and put a DEFINITE ARTICLE on them.
 * "Over the shallows" and "Over the reef" read as somewhere: a named ground a player could
 * look for on the sheet and sail to. They are not. They are a reading of a scalar field
 * taken at the boat, and the same reading is taken again a league away.
 *
 * MEASURED, over `src/voyage` and `src/sim`: there is no depth field, no sounding and no
 * seabed anywhere in the simulation - n = 0 files define one. "The reef" was the worse of
 * the two, because a reef is a STRUCTURE and the renderer draws none; a player told they are
 * over a reef looks for it. `reef`'s archetype is the WARM one (chill 0.2 against cold's
 * 0.92), so warm is what the field actually says, and saying it asserts no object.
 *
 * So all four now name a quality of the water, which is exactly what the field is, and none
 * of them names a place. The region FICTION - what these waters are called, what lives in
 * them - is still `world-places` and `story` to rule on, and lands here as four strings.
 *
 * -- THE TRAP THAT WAS HERE, AND WHY THE TYPE IS NOW THE FENCE ----------------
 *
 * This was `Record<string, string>` behind `?? 'At sea'`. `WATER_REGIONS` in regions.ts is a
 * four-entry `as const` and `fix.region` is the union it produces, so the two agreed only by
 * coincidence: a FIFTH water id would have compiled, passed every test, and printed "At sea"
 * to a player over water the simulation had a name for. Nothing anywhere went red. That was
 * MEASURED by the round that named the five sea areas and it is the reason it did not widen
 * the water table.
 *
 * `Record<WaterRegionId, string>` makes the compiler the fence: a fifth id is a missing
 * property here and `tsc` fails on it. The fallback is gone with it, because a total record
 * indexed by its own key type cannot miss, and a fallback that cannot fire is a fallback that
 * hides the next one that could.
 */
const REGION_PHRASE: Readonly<Record<WaterRegionId, string>> = {
  shallows: 'Over shallow water',
  reef: 'Over warm water',
  deep: 'Over deep water',
  cold: 'Over cold water',
};

/** Two units at most, largest first, and never a bare unit with a zero in front of it. */
export function spanWords(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const parts: string[] = [];
  const push = (n: number, unit: string): void => {
    if (n > 0) parts.push(`${n} ${unit}${n === 1 ? '' : 's'}`);
  };
  push(days, 'day');
  push(hours, 'hour');
  if (parts.length < 2) push(mins, 'minute');
  if (parts.length === 0) return 'under a minute';
  return parts.slice(0, 2).join(' ');
}

/**
 * THE PHRASE A JOURNEY REPLACES, and why it replaces the whole line rather than joining it.
 *
 * A boat under orders is not doing two things. "Over cold water, making north-west, 2 hours
 * off" is three clauses to say one fact, and the fact that matters while a leg is in force is
 * the leg: where it ends and when. The region comes back the moment the boat has its own
 * course again, which is also the moment the strike leaves the sheet.
 *
 * IT STILL NAMES NOTHING THE PLAYER HAS NOT FOUND, and `isNamed` is the whole of that gate.
 * Every label in `places.ts` clears it now that CONTRACT 1.5m has landed, so a leg under orders
 * wears its destination's name; a leg to somewhere the voyage layer publishes no name for says
 * which way the boat is heading instead, the same withholding `asideFor` does.
 */
function journeyPhrase(view: VoyageView, labelOf: (id: string) => string | null): string | null {
  const j = view.fix.journey;
  if (j === null) return null;
  const to = j.destinationPlaceId === null ? null : labelOf(j.destinationPlaceId);
  const where = isNamed(to) ? `Making for ${to}` : `Making ${compass(bearingDeg(view.fix.position, j.destination))}`;
  const leftMs = Math.max(0, j.durationMs - j.elapsedMs);
  return leftMs < 60_000 ? `${where}, nearly there.` : `${where}, ${spanWords(leftMs)} off.`;
}

/** Where the boat is and what it is doing, in one line. */
export function phraseFor(view: VoyageView, labelOf: (id: string) => string | null): string {
  const underWay = journeyPhrase(view, labelOf);
  if (underWay !== null) return underWay;
  const fix = view.fix;
  const moored = fix.mooredAt === null ? null : labelOf(fix.mooredAt);
  const where =
    fix.mooredAt !== null
      ? isNamed(moored)
        ? `At ${moored}`
        : 'At anchor'
      : REGION_PHRASE[fix.region];

  // A journey is handled above and returns the whole line, so nothing here has to describe
  // one. What is left is a boat living its own progression, which is every other state.
  let doing: string;
  if (fix.mooredAt !== null) doing = 'made fast';
  else if (fix.standing === 'lying') doing = 'lying to';
  else if (fix.standing === 'homeward') doing = 'standing homeward';
  else if (fix.standing === 'seaward') doing = 'standing seaward';
  else doing = `drifting ${compass(fix.headingDeg)}`;

  return `${where}, ${doing}`;
}

const HOUR_MS = 3_600_000;

/**
 * WHEN, AND NEVER HOW FAR. The one clause this round exists to correct.
 *
 * -- WHAT THE OLD CLAUSE SAID, AND WHAT IT MEASURED --------------------------
 *
 * It said "656 leagues off". It measured `trackLeaguesRemaining` - arc length still to be
 * DRIFTED along the spiral before the next UNDISCOVERED place enters the sighting radius.
 * A progression figure wearing a distance unit, and the three things measured about it on a
 * real save (seed 4242, `chart-units.test.ts` sweeps the same states):
 *
 *   IT IS LARGEST EXACTLY WHEN THE PLAYER IS TOUCHING LAND. Made fast at place-01 the boat
 *   lies 3.2 leagues off that island's drawn body and the line read "2978 leagues off" - a
 *   factor of 931. Made fast at Murrin Roost: 1.9 leagues of water, "691 leagues off", a
 *   factor of 363. That is the screenshot the owner sent.
 *
 *   IT IS NOT MONOTONE AND IT IS NOT BOUNDED BY THE SEA. Across the arrival at place-01 it
 *   goes 19 -> 2978 in one thirty-six-second step, because "next" becomes a place further
 *   round the spiral. Land is reported RECEDING BY 2,959 LEAGUES at the instant the boat
 *   ties up alongside it. Nothing that is a distance can do that.
 *
 *   READ CHARITABLY AS TRACK LEAGUES IT IS STILL NOT THE WATER. The spiral loops, so at
 *   h=0.2 it read 500 while the island on screen was 18.6 leagues away - 27 times over.
 *
 * -- WHY THE FIX IS THE UNIT AND NOT A GEOMETRY -------------------------------
 *
 * Distance to the nearest drawn land IS now measurable and IS cheap - `PlaceSeed.extent`
 * publishes every body and a box distance over twenty of them is nothing. It is not what
 * this line should carry. MEASURED over 400 hours at 0.02 h, 20,001 samples: land is within
 * one frame width of the boat in 7.8% of them, and that boundary is crossed 1,064 times.
 * That 7.8% is an OVER-estimate of what is on screen, learned by looking rather than by
 * arithmetic: the frame is 24 leagues wide but only 13.5 tall, so a shore 9.8 leagues off
 * the bow was photographed with no land drawn anywhere in the picture. A gate on distance
 * alone would have promised the player land they could not see. A
 * line whose SHAPE changes a thousand times an evening is worse copy than a steady one, and
 * in the other 92.2% the honest answer is a number between 40 and 275 leagues that a boat
 * with no steering cannot act on. The line's job, wayfinding clause W4, is a PROMISE -
 * something is coming, this way, and roughly when - and a time is the unit that promise is
 * made in. `journeyPhrase` above already ships this exact sentence shape for a leg under
 * orders, so nothing here is a new word.
 *
 * -- AND THE SETTLED HALF OF THE PROMISE WAS MISSING ALTOGETHER ---------------
 *
 * The settled branch printed a bare bearing and no "when" at all. MEASURED on the same
 * sweep: `settled` is the readout in 18,641 of 20,001 samples, 93.2%, so for all but the
 * first forty hours the chart made half of W4's promise and kept the other half to itself,
 * while `SettledReadout.modelledHoursToNext` sat published and unread. Both branches now
 * make the same promise in the same shape, which is what chart-view.ts says they are for.
 *
 * MEASURED RANGE of both figures over that sweep: 0.0033 to 2.92 hours. Everything this
 * clause can say is between "under a minute" and "2 hours 55 minutes".
 */
function whenClause(hours: number): string {
  const ms = Math.max(0, hours) * HOUR_MS;
  // Under a minute the figure is noise and the fact is that it is close. `journeyPhrase`
  // draws the same line at the same place and says "nearly there"; a drifting boat is not
  // going anywhere, so what is nearly true of it is the sighting, which is the event this
  // whole readout counts down to.
  return ms < 60_000 ? 'nearly in sight' : `${spanWords(ms)} off`;
}

/**
 * The quiet second line: what the sea is about to give up, without naming it early.
 *
 * ONE SHAPE, TWO STATES. Inside the charted sea the next place is by definition one the
 * player has not raised, so it is "Land"; on the settled round it is a place they have been
 * to, so it wears its name. The bearing was always a true bearing to a real charted
 * position and is untouched - the defect was never in that clause.
 */
export function asideFor(view: VoyageView, labelOf: (id: string) => string | null): string {
  const w = view.chart.wayfinding;
  if (w.kind === 'settled') {
    const name = labelOf(w.nextPlaceId);
    const subject = isNamed(name) ? name : 'Land';
    return `${subject} bears ${compass(w.bearingDeg)}, ${whenClause(w.modelledHoursToNext)}.`;
  }
  return `Land bears ${compass(w.bearingDeg)}, ${whenClause(w.modelledHoursRemaining)}.`;
}

/**
 * THE HOLD'S DISCLOSURE, or null when nothing has been skipped.
 *
 * CONTRACT section 6: time beyond the hold is "skipped and disclosed, never paid at a
 * reduced rate". Round 3 computed it, persisted it and never gave a surface a way to say
 * it; this is that surface. One sentence of mechanism, one of fact, no apology.
 */
export function holdLineFor(view: VoyageView): string | null {
  if (!view.hold.hasSkipped) return null;
  return `The hold keeps ${view.hold.holdHours} hours. This save has skipped ${spanWords(view.hold.skippedMs)}.`;
}

/** Which animal the chart draws: the target if one is set, else what the save lands most. */
export function quarryOf(state: SimState): string | null {
  if (state.targetSpecies !== '' && SPECIES.some((s) => s.id === state.targetSpecies)) return state.targetSpecies;
  let best: string | null = null;
  let bestN = 0;
  for (const s of SPECIES) {
    const n = state.catches[s.id] ?? 0;
    if (n > bestN) {
      bestN = n;
      best = s.id;
    }
  }
  return best;
}

/** Every species this build ships, landed or not. Locked rows carry the level, not a count. */
export function ledgerRows(state: SimState): LedgerRow[] {
  const level = levelForXp(state.xp);
  return SPECIES.map((s) => {
    const reached = level >= s.minLevel;
    return {
      name: s.name,
      count: reached ? (state.catches[s.id] ?? 0) : null,
      level: s.minLevel,
      marked: reached && state.targetSpecies === s.id,
      reachable: reached,
      // A species row carries no note: the fish IS the note, drawn at the pole and, when all
      // twenty-four plates exist, in the plate box. Writing a second description of it beside
      // the drawing is the two-fidelity-tiers failure this file already refuses.
      note: '',
    };
  });
}

/**
 * WHAT EACH ISLAND IS FOR, WRITTEN DOWN.
 *
 * -- THE FINDING THIS ANSWERS, IN ITS OWN WORDS ------------------------------
 *
 * "Every row still carries the same two fields in the same shape, name left and leagues
 * right, and the leaf still gives no reason to prefer one landfall over another." Twenty
 * places have names and written economies since CONTRACT 1.5m; the sheet showed none of it,
 * and it did not say which of the five AREAS a place was in either.
 *
 * -- IT IS A CLAIM ABOUT WRITING, NOT A SECOND SOURCE OF TRUTH ---------------
 *
 * `areaAt` in regions.ts derives the area from position and is the only thing the game reads.
 * The `area` word here is a copy of that derivation IN THE SHEET'S OWN SPELLING, and
 * `chart-places.test.ts` holds every entry to `areaAt(place.position)` - so a place that moves,
 * or an archetype that shifts, turns this file red rather than quietly describing the wrong sea.
 * It is the same arrangement `places.ts` `AREA_BY_PLACE` already uses and for the same reason.
 *
 * IT IS A TABLE AND NOT A CALL BECAUSE OF THE STEP BUDGET. `pageAt` runs every step, shut or
 * open, so deriving the area for twenty rows sixty times a second would put `fieldsAt` - two
 * noise samples and five archetype distances - inside the idle loop this lane is measured on.
 * A lookup costs nothing and the test pays the difference.
 *
 * THE PROSE IS `gauntlet/island/REGIONS.md` COMPRESSED, not invented beside it: one trade per
 * island, the thing that trade physically needs, and the two places where one island's product
 * is another island's material - Cloven Rigg roofs Murrin Roost, Blackstage fuels Lanterna -
 * because a sea whose islands need each other is the first thing this chart should say about it.
 */
export interface PlaceNote {
  /** The area, spelled the way the sheet writes it. Held to `areaAt` by the test. */
  readonly area: string;
  /** What the island is for, in two or three words. This is the row's own middle column. */
  readonly trade: string;
  /** The same island in one sentence, for the foot of the page and the plain-type row. */
  readonly says: string;
}

export const PLACE_NOTES: ReadonlyMap<string, PlaceNote> = new Map([
  ['Murrin Roost', { area: 'Wrack', trade: 'lichen purple', says: 'Home, and a lichen purple that holds in salt water.' }],
  ['Tangsay', { area: 'Wrack', trade: 'kelp ash', says: 'Weed burned in shore-long pits to an ash for glass and soap.' }],
  ['Cloven Rigg', { area: 'Wrack', trade: 'slate', says: 'One bed of slate, and the roofs of Murrin Roost come off it.' }],
  ['Ederholm', { area: 'Wrack', trade: 'eider down', says: 'Eider down, gathered after the ducks have gone.' }],
  ['Larchmouth', { area: 'Wrack', trade: 'hulls', says: 'Hulls, walked out of one open shed onto a spring tide.' }],
  ['Marrow Bar', { area: 'Shell Banks', trade: 'oysters', says: 'Oysters on staked beds you can walk at low water.' }],
  ['Nacre Scaur', { area: 'Shell Banks', trade: 'cut shell', says: 'Shell cut for buttons and inlay; the pearls are a rumour.' }],
  ['Alderflet', { area: 'Shell Banks', trade: 'smoked fish', says: 'Other boats bring the fish; this island smokes it.' }],
  ['Woolstrand', { area: 'Shell Banks', trade: 'sponges', says: 'Sponges dived off a bank, and every roof is a drying floor.' }],
  ['Warden Bar', { area: 'Shell Banks', trade: 'pilotage', says: 'Pilotage. Eleven families read the shoals for a fee.' }],
  ['Torsk Vard', { area: 'Rime', trade: 'stockfish', says: 'Cod hung in a freezing dry wind, and it keeps three years.' }],
  ['Cleitness', { area: 'Sunder', trade: 'seabirds', says: 'Seabirds off nine hundred feet of cliff, on ropes of hide.' }],
  ['Kell Skerry', { area: 'Sunder', trade: 'the records', says: 'No trade. A dozen people write down what comes past.' }],
  ['Blackstage', { area: 'Sunder', trade: 'lamp oil', says: 'Liver oil, and Lanterna burns half of what leaves here.' }],
  ['Hafdyke', { area: 'Sunder', trade: 'wool', says: 'Wool off sheep that crop the wrack outside the wall.' }],
  ['Pitchgarth', { area: 'Sunder', trade: 'repairs', says: 'The one bay out here a hull can be laid over and re-tarred.' }],
  ['Salina', { area: 'Glass', trade: 'salt', says: 'Salt raked off pans in a lagoon that has not seen rain.' }],
  ['Cisterna', { area: 'Glass', trade: 'fresh water', says: 'The only deep spring for two hundred leagues; nobody lands.' }],
  ['Almadra', { area: 'Glass', trade: 'tuna in season', says: 'Tuna for six weeks, and empty net lofts for the other ten months.' }],
  ['Lanterna', { area: 'Glass', trade: 'night fish', says: 'Night fish, worked by lamp on oil out of Blackstage.' }],
]);

/**
 * THE SECOND LEAF: every place the boat has raised, nearest first.
 *
 * IT PRINTS A BEARING WHERE A NAME WOULD GO when the voyage layer publishes no name, and that
 * is the fiction working rather than a gap in it: CONTRACT 1.5h(a) rules that "the chart slowly
 * learns names as you fish", so an unnamed landfall is a place you have seen and not yet named.
 * All twenty clear `isNamed` today; the gate is what keeps the twenty-first honest.
 *
 * -- THREE FIELDS, NOT TWO, AND WHY THE MIDDLE ONE IS WASH -------------------
 *
 * The name is INK, the note is WASH, the figure is INK. That is the sheet's existing pair of
 * written roles and no third one: a note is not a heading and is not a figure, it is the same
 * subordinate voice the foot and the locked species rows are already written in. An open
 * finding says the standing foot line and the passing aside are indistinguishable by eye; this
 * adds nothing to that family, because it is separated from the name it belongs to by POSITION
 * (a fixed column) rather than by tone.
 *
 * The area leads the note so the five areas form a readable column down the leaf. That column
 * repeats - "Sunder" five times, "Rime" once - and the repetition is the point, which is what
 * makes it different in kind from the bearing this leaf used to print at that position. A
 * bearing is a property of WHERE THE BOAT IS and changed as it drifted, so it could never
 * individuate anything; an area is a property of the place and groups twenty rows into five.
 *
 * The figure is LEAGUES OFF, in the tally, and the foot below says the unit out loud - which is
 * this lane's own rule from round 4, a figure wearing the unit of the thing it measures, applied
 * to the one figure on the sheet that was wearing none.
 */
export function placeRows(rows: readonly ChartRow[]): LedgerRow[] {
  return rows.map((r) => {
    const named = isNamed(r.label);
    const note = named ? PLACE_NOTES.get(r.label) : undefined;
    return {
      name: named ? r.label : capitalise(compass(r.bearingDeg)),
      count: Math.max(0, Math.round(r.distanceLeagues)),
      level: 0,
      marked: r.isHere,
      reachable: true,
      note: note === undefined ? '' : `${note.area}, ${note.trade}`,
    };
  });
}

/**
 * THE FOOT OF THE PLACES LEAF: the landfall under the hand, said in one sentence.
 *
 * -- IT COSTS NO SPACE, WHICH IS WHY IT IS HERE ------------------------------
 *
 * The foot is one line and takes one job at a time. Shut, it is the standing account. Open on
 * the species leaf it is that page's key, because the mastery measure under a row is otherwise
 * a mark with no explanation. Open on the PLACES leaf it was the EMPTY STRING - a whole line of
 * the sheet doing nothing on one of its two pages - and the page it was blank on is the page
 * with a written world behind it and no room on a row to put any of it.
 *
 * -- WHAT IT SAYS, AND THE ONE THING IT SAYS THAT NOTHING ELSE DOES ----------
 *
 * The area, the distance WITH ITS UNIT, and what the island is for. The unit is the part no
 * other pixel on this sheet carried: the species leaf's tally is a landed count and the places
 * leaf's tally is leagues, they are drawn identically, and nothing said which was which. This
 * lane's rule from round 4 is that a figure wears the unit of the thing it measures, and it was
 * written after a figure that wore the wrong one; a figure wearing none is the same defect one
 * step earlier.
 *
 * The name is repeated from the row on purpose. The plain-type page reads this line on its own,
 * and a sentence beginning "in the Glass, 505 leagues off" has no subject for a reader who
 * cannot see where the hand is pointing.
 */
export function placeFootLine(row: LedgerRow | undefined): string {
  if (row === undefined || row.count === null) return '';
  const note = PLACE_NOTES.get(row.name);
  if (note === undefined) return '';
  return `${row.name}, in the ${note.area}, ${row.count} leagues off. ${note.says}`;
}

function capitalise(word: string): string {
  return word.length === 0 ? word : word[0]!.toUpperCase() + word.slice(1);
}

/**
 * WHAT A LOCKED ROW SAYS WHEN A PLAYER TOUCHES IT.
 *
 * The row already carries "lv 81", and a number is not an answer to why nothing happened. A
 * greyed row that stays silent when you press it is the single most common way an interface
 * teaches a player that it is broken, so the chart says the thing the number means, in the
 * voice it says everything else in: one sentence, no apology, no exclamation.
 */
export function lockedLine(speciesId: string): string | null {
  const s = speciesById(speciesId);
  if (s === undefined) return null;
  return `${s.name} does not come to the line until level ${s.minLevel}.`;
}

/** What it says when the choice lands. The mark on the strip has already changed. */
export function chosenLine(speciesId: string): string | null {
  const s = speciesById(speciesId);
  if (s === undefined) return null;
  return `Now fishing for ${s.name.toLowerCase()}.`;
}

/**
 * The two ways the boat can refuse a destination, said rather than swallowed.
 *
 * `sailTo` refuses and never throws, which means a surface that ignores the result shows a
 * player a selection that silently did nothing. Both reasons are reachable from a stale
 * ledger - a row drawn before the save was replaced - so both get a sentence.
 */
export function refusalLine(reason: SailRefusalReason): string {
  return reason === 'not-discovered'
    ? 'That water has not been raised yet.'
    : 'There is nothing charted there.';
}
